import { useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import type { DocumentKind, DocumentRewriteResponse } from '@/types/document-rewrite';

/**
 * Lazy plain-language rewrite of one document. Nothing is fetched until
 * `fetch()` is called (opening the row); the result is kept for the session
 * so the list's gist and a re-opened document read the same cache entry.
 * A failed call is not retried automatically; re-opening the document tries again.
 */
export function useDocumentRewrite(kind: DocumentKind, id: string, title: string, text: string) {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage === 'es' ? 'es' : 'en';

  const query = useQuery<DocumentRewriteResponse>({
    queryKey: ['/api/rewrite-document', kind, id, language],
    queryFn: () => api.rewriteDocument({ kind, id, title, text, language }),
    enabled: false,
    staleTime: Infinity,
    retry: false,
  });

  // Stable trigger so a mount effect runs it exactly once per open.
  const queryRef = useRef(query);
  queryRef.current = query;
  const fetch = useCallback(() => {
    const current = queryRef.current;
    if (current.data === undefined && !current.isFetching) void current.refetch();
  }, []);

  return { rewrite: query.data ?? null, isFetching: query.isFetching, isError: query.isError, fetch };
}
